import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getClientSchedules,
  createClientSchedule,
  updateClientSchedule,
  deleteClientSchedule,
  setClientTimezone,
} from '../api/client';
import {
  DAY_LABELS,
  TIMEZONE_OPTIONS,
  type Schedule,
  type ScheduleInput,
  type SchedulesResponse,
} from '../api/types';
import { useAuth } from '../auth/useAuth';
import { Button } from './Button';
import { Card } from './Card';

interface FormState {
  days: boolean[]; // length 7, 0=Sun..6=Sat
  windowStart: string; // HH:MM
  windowEnd: string;
  label: string;
}

const BLANK: FormState = {
  days: [false, true, true, true, true, true, false], // weekdays
  windowStart: '09:00',
  windowEnd: '10:00',
  label: '',
};

function to12h(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function summarizeDays(days: number[]): string {
  const set = new Set(days);
  const all = days.length === 7;
  const weekdays = days.length === 5 && [1, 2, 3, 4, 5].every((d) => set.has(d));
  const weekend = days.length === 2 && set.has(0) && set.has(6);
  if (all) return 'Every day';
  if (weekdays) return 'Weekdays';
  if (weekend) return 'Weekends';
  return [...days].sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(', ');
}

export function ScheduleSection({ memberId, editable }: { memberId: string; editable: boolean }) {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['client-schedules', memberId];

  const q = useQuery({
    queryKey,
    queryFn: () => getClientSchedules(memberId, getIdToken),
  });

  const [editing, setEditing] = useState<string | null>(null); // scheduleId | 'new' | null
  const [form, setForm] = useState<FormState>(BLANK);
  const [formError, setFormError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [tzError, setTzError] = useState<string | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey });
  }

  const saveMutation = useMutation({
    mutationFn: (vars: { scheduleId: string | null; input: ScheduleInput }) =>
      vars.scheduleId
        ? updateClientSchedule(memberId, vars.scheduleId, vars.input, getIdToken)
        : createClientSchedule(memberId, vars.input, getIdToken),
    onSuccess: () => {
      setEditing(null);
      setNote('Saved.');
      invalidate();
    },
    onError: () => setFormError('Could not save. Please check the days and times and try again.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (scheduleId: string) => deleteClientSchedule(memberId, scheduleId, getIdToken),
    onSuccess: () => {
      setNote('Schedule removed.');
      invalidate();
    },
    onError: () => setNote('Could not remove that schedule. Please try again.'),
  });

  const timezoneMutation = useMutation({
    mutationFn: (tz: string) => setClientTimezone(memberId, tz, getIdToken),
    onSuccess: (res) => {
      setTzError(null);
      setNote('Timezone updated.');
      queryClient.setQueryData<SchedulesResponse>(queryKey, (old) =>
        old ? { ...old, timezone: res.timezone } : old,
      );
    },
    onError: () => setTzError('Could not update the timezone. Please try again.'),
  });

  function openNew() {
    setForm(BLANK);
    setFormError(null);
    setNote(null);
    setEditing('new');
  }

  function openEdit(s: Schedule) {
    setForm({
      days: Array.from({ length: 7 }, (_, i) => s.daysOfWeek.includes(i)),
      windowStart: s.windowStart,
      windowEnd: s.windowEnd,
      label: s.label ?? '',
    });
    setFormError(null);
    setNote(null);
    setEditing(s.scheduleId);
  }

  function submit() {
    const daysOfWeek = form.days.map((on, i) => (on ? i : -1)).filter((i) => i >= 0);
    if (daysOfWeek.length === 0) {
      setFormError('Pick at least one day.');
      return;
    }
    if (form.windowEnd <= form.windowStart) {
      setFormError('The end time must be after the start time.');
      return;
    }
    setFormError(null);
    saveMutation.mutate({
      scheduleId: editing === 'new' ? null : editing,
      input: {
        daysOfWeek,
        windowStart: form.windowStart,
        windowEnd: form.windowEnd,
        label: form.label.trim() || null,
      },
    });
  }

  if (q.isLoading) return <Card className="mb-6 p-4 text-[14px] text-hs-text-mute">Loading schedule…</Card>;
  if (q.error || !q.data) {
    return (
      <Card className="mb-6 p-4">
        <p role="alert" className="text-[13px] text-hs-danger-text">
          We couldn't load the call schedule right now.
        </p>
      </Card>
    );
  }

  const { timezone, schedules } = q.data;
  const tzInList = TIMEZONE_OPTIONS.some((o) => o.value === timezone);

  return (
    <Card className="mb-6 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute">Call schedule</h2>
        {editable && editing === null && (
          <Button variant="subtle" onClick={openNew}>
            Add a schedule
          </Button>
        )}
      </div>

      {/* Timezone */}
      <div className="flex flex-col gap-1">
        <label htmlFor="member-timezone" className="text-[13px] text-hs-text-mute">
          Times are in the member's timezone
        </label>
        <select
          id="member-timezone"
          className="w-full max-w-xs rounded-hs-md border border-hs-border bg-hs-surface px-3 py-2 text-[14px] text-hs-text disabled:opacity-60"
          value={timezone}
          disabled={!editable || timezoneMutation.isPending}
          onChange={(e) => timezoneMutation.mutate(e.target.value)}
        >
          {!tzInList && <option value={timezone}>{timezone}</option>}
          {TIMEZONE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {tzError && (
          <p role="alert" className="text-[13px] text-hs-danger-text">
            {tzError}
          </p>
        )}
      </div>

      {/* List */}
      {schedules.length === 0 && editing === null ? (
        <p className="text-[14px] text-hs-text-mute">
          No recurring calls scheduled. {editable ? 'Add one to start daily check-ins.' : ''}
        </p>
      ) : (
        <ul className="flex flex-col gap-2 list-none p-0 m-0">
          {schedules.map((s) => (
            <li
              key={s.scheduleId}
              className="flex items-center justify-between gap-3 rounded-hs-md border border-hs-border p-3"
            >
              <div>
                <p className="text-[14px] font-semibold text-hs-text">
                  {summarizeDays(s.daysOfWeek)}
                </p>
                <p className="text-[13px] text-hs-text-mute">
                  Sometime between {to12h(s.windowStart)} and {to12h(s.windowEnd)}
                  {s.label ? ` · ${s.label}` : ''}
                </p>
              </div>
              {editable && (
                <div className="flex gap-1.5">
                  <Button variant="ghost" onClick={() => openEdit(s)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    loading={deleteMutation.isPending && deleteMutation.variables === s.scheduleId}
                    onClick={() => {
                      if (window.confirm('Remove this call schedule?')) deleteMutation.mutate(s.scheduleId);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add / edit form */}
      {editable && editing !== null && (
        <div className="rounded-hs-md border border-hs-border bg-hs-surface-alt p-3 flex flex-col gap-3">
          <p className="text-[13px] font-semibold text-hs-text">
            {editing === 'new' ? 'New schedule' : 'Edit schedule'}
          </p>

          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute">Days</span>
            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((label, i) => {
                const on = form.days[i];
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setForm((f) => ({ ...f, days: f.days.map((d, j) => (j === i ? !d : d)) }))
                    }
                    className={
                      'w-11 h-9 rounded-hs-md border text-[13px] font-semibold ' +
                      (on
                        ? 'border-hs-primary bg-hs-primary text-white'
                        : 'border-hs-border bg-hs-surface text-hs-text')
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex flex-col gap-1 text-[13px] text-hs-text-mute">
              Earliest
              <input
                type="time"
                value={form.windowStart}
                onChange={(e) => setForm((f) => ({ ...f, windowStart: e.target.value }))}
                className="rounded-hs-md border border-hs-border bg-hs-surface px-3 py-2 text-[14px] text-hs-text"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px] text-hs-text-mute">
              Latest
              <input
                type="time"
                value={form.windowEnd}
                onChange={(e) => setForm((f) => ({ ...f, windowEnd: e.target.value }))}
                className="rounded-hs-md border border-hs-border bg-hs-surface px-3 py-2 text-[14px] text-hs-text"
              />
            </label>
          </div>
          <p className="text-[12px] text-hs-text-mute">
            We place the call at a natural, random time within this window.
          </p>

          <label className="flex flex-col gap-1 text-[13px] text-hs-text-mute">
            Label (optional)
            <input
              type="text"
              value={form.label}
              placeholder="e.g. Morning check-in"
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className="max-w-xs rounded-hs-md border border-hs-border bg-hs-surface px-3 py-2 text-[14px] text-hs-text"
            />
          </label>

          {formError && (
            <p role="alert" className="text-[13px] text-hs-danger-text">
              {formError}
            </p>
          )}

          <div className="flex gap-2">
            <Button variant="primary" loading={saveMutation.isPending} onClick={submit}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => { setEditing(null); setFormError(null); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {note && editing === null && <p className="text-[13px] text-hs-success-text">{note}</p>}
    </Card>
  );
}
